/**
 * Primitivos compartilhados pelas telas comerciais — Campanhas, Leads,
 * Usuários e Configurações.
 *
 * Por que este arquivo existe, e não mais componentes em `base.tsx`: `base`
 * é o vocabulário de toda a plataforma e tem outros donos. O que está aqui é
 * específico do recorte comercial (funil, veredito com motivo, gaveta de
 * formulário) e só estas quatro telas usam.
 *
 * REGRA DE COR: nenhum hexadecimal, nenhuma cor padrão do Tailwind. Só os
 * tokens do design system. As classes aparecem **literais** nos mapas abaixo
 * porque o Tailwind v4 varre o código-fonte — classe montada por template
 * string (`bg-${tom}-suave`) simplesmente não é gerada.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { FunilCampanha } from "@co/contracts";
import type { Carga } from "../api.js";
import { Botao, Carregando, Vazio, type Tom } from "./base.js";

/* ══════════════════════════════════════════════════════════════════════
   Carga — estado explícito de carregando / pronto / erro
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Busca com estado explícito e recarga sob demanda.
 *
 * `buscar` PRECISA ser estável (`useCallback`): sem isso a identidade muda a
 * cada render e o efeito vira laço infinito de requisição. A flag `vivo`
 * existe porque resposta atrasada de uma busca abandonada não pode
 * sobrescrever o resultado da busca atual.
 */
export function useCarga<T>(buscar: () => Promise<T>): {
  estado: Carga<T>;
  recarregar: () => void;
} {
  const [estado, setEstado] = useState<Carga<T>>({ fase: "carregando" });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let vivo = true;
    // Recarga não pisca: enquanto a nova resposta não chega, o que já está
    // na tela continua lá. Trocar dado bom por "Carregando…" a cada ação faz
    // o operador perder o lugar onde estava lendo.
    setEstado((atual) =>
      atual.fase === "pronto" ? atual : { fase: "carregando" },
    );
    buscar()
      .then((dados) => {
        if (vivo) setEstado({ fase: "pronto", dados });
      })
      .catch((e: unknown) => {
        if (!vivo) return;
        setEstado({
          fase: "erro",
          mensagem:
            e instanceof Error ? e.message : "Não foi possível carregar.",
        });
      });
    return () => {
      vivo = false;
    };
  }, [buscar, tick]);

  const recarregar = useCallback(() => setTick((t) => t + 1), []);
  return { estado, recarregar };
}

/** Erro nunca é tela em branco: mostra a mensagem da API e oferece repetir. */
export function ErroDeCarga({
  mensagem,
  aoTentarDeNovo,
}: {
  mensagem: string;
  aoTentarDeNovo?: () => void;
}): JSX.Element {
  return (
    <div
      role="alert"
      className="rounded-[10px] bg-crit-suave text-crit-tinta px-4 py-3.5 text-[13px] leading-relaxed flex items-center gap-3 flex-wrap"
    >
      <span>{mensagem}</span>
      {aoTentarDeNovo && (
        <span className="ml-auto">
          <Botao pequeno onClick={aoTentarDeNovo}>
            Tentar de novo
          </Botao>
        </span>
      )}
    </div>
  );
}

/**
 * Os três estados de uma carga em um só lugar. Cada tela declara só o
 * conteúdo do caso feliz e o que conta como "vazio" — o resto é igual em
 * todas, e igual é o que o operador consegue aprender uma vez.
 */
export function Conteudo<T>({
  estado,
  aoRecarregar,
  vazio,
  mensagemVazio = "Nada por aqui ainda.",
  children,
}: {
  estado: Carga<T>;
  aoRecarregar?: () => void;
  vazio?: (dados: T) => boolean;
  mensagemVazio?: ReactNode;
  children: (dados: T) => ReactNode;
}): JSX.Element {
  if (estado.fase === "carregando") return <Carregando />;
  if (estado.fase === "erro") {
    return (
      <ErroDeCarga
        mensagem={estado.mensagem}
        {...(aoRecarregar ? { aoTentarDeNovo: aoRecarregar } : {})}
      />
    );
  }
  if (vazio?.(estado.dados)) return <Vazio>{mensagemVazio}</Vazio>;
  return <>{children(estado.dados)}</>;
}

/* ══════════════════════════════════════════════════════════════════════
   Funil da campanha — invariante 1 desenhada
   ══════════════════════════════════════════════════════════════════════ */

const FAIXA: Record<"ok" | "crit" | "warn" | "idle", string> = {
  ok: "bg-ok-suave text-ok-tinta",
  crit: "bg-crit-suave text-crit-tinta",
  warn: "bg-warn-suave text-warn-tinta",
  idle: "bg-idle-suave text-idle-tinta",
};

/**
 * Enviado · falhou · ignorado · pendente, proporcional ao volume.
 *
 * A soma dos quatro é a base total — invariante 1. Quando `integro` é falso
 * a barra ganha contorno crítico: um funil bonito que não fecha a conta é
 * pior do que nenhum funil, porque passa confiança que não existe.
 */
export function BarraFunil({ funil }: { funil: FunilCampanha }): JSX.Element {
  const segmentos = [
    { n: funil.enviado, tom: "ok" as const, rotulo: "Enviados" },
    { n: funil.falhou, tom: "crit" as const, rotulo: "Falharam" },
    { n: funil.ignorado, tom: "warn" as const, rotulo: "Ignorados" },
    { n: funil.pendente, tom: "idle" as const, rotulo: "Pendentes" },
  ].filter((s) => s.n > 0);

  const descricao = `Base de ${funil.total}: ${funil.enviado} enviados, ${funil.falhou} falharam, ${funil.ignorado} ignorados, ${funil.pendente} pendentes.`;

  return (
    <div
      role="img"
      aria-label={descricao}
      title={descricao}
      className={`flex gap-0.5 h-5 rounded-md overflow-hidden bg-superficie-3 ${
        funil.integro ? "" : "ring-1 ring-crit"
      }`}
    >
      {segmentos.length === 0 ? (
        <span className="grid place-items-center w-full text-[10.5px] text-tinta-3">
          base vazia
        </span>
      ) : (
        segmentos.map((s) => (
          <span
            key={s.tom}
            style={{ flex: s.n }}
            className={`grid place-items-center font-mono text-[10.5px] font-semibold min-w-0 overflow-hidden ${FAIXA[s.tom]}`}
          >
            {s.n}
          </span>
        ))
      )}
    </div>
  );
}

/** Legenda do funil. Cor sozinha não informa — nem para quem não distingue. */
export function LegendaFunil(): JSX.Element {
  const itens: Array<[keyof typeof FAIXA, string]> = [
    ["ok", "Enviados"],
    ["crit", "Falharam"],
    ["warn", "Ignorados"],
    ["idle", "Pendentes"],
  ];
  return (
    <div className="flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-tinta-3">
      {itens.map(([tom, rotulo]) => (
        <span key={tom} className="inline-flex items-center gap-1.5">
          <span className={`inline-block size-2.5 rounded-sm ${FAIXA[tom]}`} />
          {rotulo}
        </span>
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Veredito — nunca só cor, nunca só número
   ══════════════════════════════════════════════════════════════════════ */

const DESTAQUE: Record<Tom, string> = {
  ok: "bg-ok-suave text-ok-tinta",
  ember: "bg-ember-suave text-ember-tinta",
  warn: "bg-warn-suave text-warn-tinta",
  crit: "bg-crit-suave text-crit-tinta",
  cool: "bg-cool-suave text-cool-tinta",
  idle: "bg-idle-suave text-idle-tinta",
  marca: "bg-marca-suave text-marca-tinta",
};

/**
 * O veredito do sistema em destaque, com a frase que o sustenta.
 *
 * Regra do projeto: "se não é explicável, não é usável". Um selo verde sem
 * motivo é exatamente o tipo de sinal que o operador aprende a ignorar.
 */
export function Veredito({
  titulo,
  motivo,
  tom = "idle",
  detalhes,
}: {
  titulo: ReactNode;
  motivo: ReactNode;
  tom?: Tom;
  detalhes?: ReactNode;
}): JSX.Element {
  return (
    <div className={`rounded-[10px] px-4 py-3.5 ${DESTAQUE[tom]}`} role="status">
      <div className="text-[13px] font-semibold tracking-tight">{titulo}</div>
      <p className="text-[13px] leading-relaxed mt-1 max-w-[80ch]">{motivo}</p>
      {detalhes && <div className="mt-2.5">{detalhes}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Estrutura
   ══════════════════════════════════════════════════════════════════════ */

/** Faixa de indicadores. Responsiva por contagem, não por breakpoint fixo. */
export function Indicadores({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
      {children}
    </div>
  );
}

/** Par rótulo/valor para leitura — o `campoLeitura` do protótipo. */
export function Leitura({
  rotulo,
  children,
  largo,
}: {
  rotulo: string;
  children: ReactNode;
  largo?: boolean;
}): JSX.Element {
  return (
    <div className={largo ? "sm:col-span-2" : ""}>
      <div className="text-[11px] font-medium text-tinta-3">{rotulo}</div>
      <div className="text-[13px] text-tinta mt-0.5 break-words">{children}</div>
    </div>
  );
}

/** Grade de formulário / leitura. Uma coluna no celular, duas ou três acima. */
export function Grade({
  children,
  colunas = 3,
}: {
  children: ReactNode;
  colunas?: 2 | 3;
}): JSX.Element {
  return (
    <div
      className={`grid gap-4 grid-cols-1 sm:grid-cols-2 ${
        colunas === 3 ? "lg:grid-cols-3" : ""
      }`}
    >
      {children}
    </div>
  );
}

/** Célula de tabela com o mesmo respiro do cabeçalho de `Tabela`. */
export function Td({
  children,
  direita,
  className = "",
}: {
  children: ReactNode;
  direita?: boolean;
  className?: string;
}): JSX.Element {
  return (
    <td
      className={`px-5 py-2.5 border-t border-linha text-[13px] align-top ${
        direita ? "text-right tabular" : ""
      } ${className}`}
    >
      {children}
    </td>
  );
}

/** Linha de "nada encontrado" dentro de uma tabela já montada. */
export function LinhaVazia({
  colunas,
  children,
}: {
  colunas: number;
  children: ReactNode;
}): JSX.Element {
  return (
    <tr>
      <td
        colSpan={colunas}
        className="px-5 py-10 text-center text-[13px] text-tinta-3 border-t border-linha"
      >
        {children}
      </td>
    </tr>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Gaveta modal — formulários
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Diálogo modal acessível: `role="dialog"`, foco inicial dentro do painel,
 * Esc fecha e o fundo escurece sem virar clicável por acidente.
 */
export function Gaveta({
  titulo,
  dica,
  aoFechar,
  rodape,
  children,
  largura = "media",
}: {
  titulo: string;
  dica?: string;
  aoFechar: () => void;
  rodape?: ReactNode;
  children: ReactNode;
  largura?: "media" | "larga";
}): JSX.Element {
  const idTitulo = useId();
  const painel = useRef<HTMLDivElement>(null);

  // O callback vive numa ref para que o efeito de montagem rode UMA vez. Se
  // ele dependesse de `aoFechar` — que a chamadora recria a cada render — o
  // efeito rodaria de novo a cada tecla digitada e o `focus()` roubaria o
  // cursor do campo que a pessoa está preenchendo.
  const fechar = useRef(aoFechar);
  useEffect(() => {
    fechar.current = aoFechar;
  }, [aoFechar]);

  useEffect(() => {
    painel.current?.focus();
    const aoTecla = (e: KeyboardEvent): void => {
      if (e.key === "Escape") fechar.current();
    };
    window.addEventListener("keydown", aoTecla);
    return () => window.removeEventListener("keydown", aoTecla);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-bg/80 p-4 sm:p-8"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) aoFechar();
      }}
    >
      <div
        ref={painel}
        role="dialog"
        aria-modal="true"
        aria-labelledby={idTitulo}
        tabIndex={-1}
        className={`w-full bg-superficie border border-linha rounded-[10px] outline-none ${
          largura === "larga" ? "max-w-4xl" : "max-w-2xl"
        }`}
      >
        <div className="px-5 py-3.5 border-b border-linha flex items-center gap-3 flex-wrap sticky top-0 bg-superficie rounded-t-[10px] z-10">
          <h2
            id={idTitulo}
            className="text-[13px] font-semibold tracking-tight"
          >
            {titulo}
          </h2>
          {dica && <span className="text-xs text-tinta-3">{dica}</span>}
          <div className="ml-auto">
            <Botao pequeno tipo="fantasma" onClick={aoFechar}>
              Fechar
            </Botao>
          </div>
        </div>
        <div className="p-5">{children}</div>
        {rodape && (
          <div className="px-5 py-3.5 border-t border-linha flex items-center gap-2 flex-wrap">
            {rodape}
          </div>
        )}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Controles
   ══════════════════════════════════════════════════════════════════════ */

/** Caixa de marcação com explicação ao lado — o "por quê" fica visível. */
export function Marcar({
  marcado,
  aoMudar,
  rotulo,
  dica,
  desabilitado,
}: {
  marcado: boolean;
  aoMudar: (v: boolean) => void;
  rotulo: ReactNode;
  dica?: ReactNode;
  desabilitado?: boolean;
}): JSX.Element {
  return (
    <label className="flex gap-2.5 items-start cursor-pointer">
      <input
        type="checkbox"
        checked={marcado}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.target.checked)}
        className="mt-0.5 size-3.5 accent-marca shrink-0"
      />
      <span className="min-w-0">
        <span className="block text-[13px] text-tinta">{rotulo}</span>
        {dica && (
          <span className="block text-[11.5px] text-tinta-3 mt-0.5 leading-relaxed">
            {dica}
          </span>
        )}
      </span>
    </label>
  );
}

/**
 * Escolha em cartões: cada opção mostra o que ela significa, não só o nome.
 * Usada onde a decisão tem consequência — travar variável do planejador,
 * formato de envio, perfil de usuário.
 */
export function Escolha<V extends string>({
  valor,
  opcoes,
  aoEscolher,
  rotuloGrupo,
}: {
  valor: V;
  opcoes: ReadonlyArray<{ id: V; nome: string; desc: string }>;
  aoEscolher: (v: V) => void;
  rotuloGrupo: string;
}): JSX.Element {
  return (
    <div
      role="radiogroup"
      aria-label={rotuloGrupo}
      className="grid gap-2 grid-cols-[repeat(auto-fit,minmax(200px,1fr))]"
    >
      {opcoes.map((o) => {
        const ativa = o.id === valor;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={ativa}
            onClick={() => aoEscolher(o.id)}
            className={`text-left rounded-[10px] border px-3.5 py-3 transition-colors ${
              ativa
                ? "border-marca bg-marca-suave text-marca-tinta"
                : "border-linha bg-superficie-2 text-tinta hover:bg-superficie-3"
            }`}
          >
            <span className="block text-[13px] font-medium">{o.nome}</span>
            <span
              className={`block text-[11.5px] mt-0.5 leading-relaxed ${
                ativa ? "" : "text-tinta-3"
              }`}
            >
              {o.desc}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════
   Formatação
   ══════════════════════════════════════════════════════════════════════ */

const DT = new Intl.DateTimeFormat("pt-BR", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
});

const D = new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" });

export function fmtDataHora(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : DT.format(d);
}

/** Dia do cronograma vem como `YYYY-MM-DD` — sem hora, sem fuso para errar. */
export function fmtDia(dia: string): string {
  const partes = dia.split("-");
  const a = Number(partes[0]);
  const m = Number(partes[1]);
  const d = Number(partes[2]);
  if (!a || !m || !d) return dia;
  return D.format(new Date(a, m - 1, d));
}

export function fmtNum(n: number): string {
  return n.toLocaleString("pt-BR");
}

export function pctDe(parte: number, total: number): number {
  return total > 0 ? Math.round((parte / total) * 100) : 0;
}

/* ══════════════════════════════════════════════════════════════════════
   Leitura defensiva de respostas ainda não tipadas no contrato
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Algumas rotas de escrita (`POST /api/campanhas`, `POST /api/campanhas/:id/base`)
 * ainda não têm schema de RESPOSTA no contrato — o cliente as devolve como
 * `unknown`. Em vez de mentir com `as`, a tela lê o que precisa com guarda de
 * tipo e trata a ausência como caso normal.
 */
export function objeto(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

export function texto(v: unknown): string | null {
  return typeof v === "string" && v.length > 0 ? v : null;
}

export function inteiro(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

/** Procura um id no corpo da resposta, aceitando `{id}` ou `{campanha:{id}}`. */
export function idDaResposta(v: unknown): string | null {
  const o = objeto(v);
  if (!o) return null;
  const direto = texto(o["id"]);
  if (direto) return direto;
  const aninhado = objeto(o["campanha"]);
  return aninhado ? texto(aninhado["id"]) : null;
}
